import type { OfferSku, PaperKingdomProject } from "../domain/schema";
import {
  getChannelListing,
  getMarketplaceGuide,
  getPrimaryFileLabel,
  getSellingKitGuide,
} from "../data/marketplaces";
import { getPromptWorkForChannel } from "./promptWork";
import {
  getCoverFileBlueprintSignature,
  getPrimaryFileBlueprintSignature,
} from "./pageBlueprint";

export interface ProgressLane {
  complete: number;
  total: number;
  percentage: number;
  label: string;
}

export interface ProjectProgress {
  checks: boolean[];
  percentage: number;
  planning: ProgressLane;
  production: ProgressLane;
  publishing: ProgressLane;
}

export interface ReadinessSection {
  title: string;
  complete: boolean;
  missing: string[];
}

function lane(checks: boolean[], labels: [string, string, string]): ProgressLane {
  const complete = checks.filter(Boolean).length;
  const total = checks.length;
  const percentage = total ? Math.round((complete / total) * 100) : 0;
  const label = complete === total ? labels[2] : complete ? labels[1] : labels[0];
  return { complete, total, percentage, label };
}

export function getProjectCompletion(
  project: PaperKingdomProject,
  entitlements: OfferSku[],
): ProjectProgress {
  const firstBook = project.catalogue[0];
  const firstCover = firstBook ? project.production.coverPlans[firstBook.id] : undefined;
  const firstInterior = firstBook ? project.production.interiorPlans[firstBook.id] : undefined;
  const firstListing = firstBook ? getChannelListing(project, firstBook.id) : undefined;
  const isBook = getMarketplaceGuide(project.marketplace).usesKdpPrintSetup;
  const sellingGuide = getSellingKitGuide(project.marketplace);
  const primaryFileCurrent = Boolean(
    firstBook &&
      project.production.primaryFileCreated &&
      project.production.primaryFilePromptSignature ===
        getPrimaryFileBlueprintSignature(project, firstBook.id),
  );
  const coverFileCurrent = Boolean(
    firstBook &&
      project.production.coverPdfCreated &&
      project.production.coverFilePromptSignature ===
        getCoverFileBlueprintSignature(project, firstBook.id),
  );

  const ideaComplete = Boolean(project.audience.trim() && project.theme.trim());
  const catalogueComplete = Boolean(
    project.catalogue.length &&
      project.catalogue.every(
        (book) => book.title.trim() && book.differentiator.trim(),
      ),
  );
  const planning = lane(
    [ideaComplete, catalogueComplete],
    isBook
      ? ["Not started", "Book ideas in progress", "Book plan ready"]
      : ["Not started", "Product idea in progress", "Product plan ready"],
  );

  const deliveryComplete = isBook
    ? Boolean(primaryFileCurrent && coverFileCurrent)
    : primaryFileCurrent;
  const productionChecks = entitlements.includes("paper-kingdom-production")
    ? [
        Boolean(
          project.production.coverSystemId &&
            project.production.coverPaletteId &&
            firstCover?.variant &&
            firstCover.backCoverCopy.trim(),
        ),
        Boolean(firstInterior?.layoutIds.length && firstInterior.sequenceNotes.trim()),
        deliveryComplete,
      ]
    : [];
  const production = lane(
    productionChecks,
    isBook
      ? ["Book-making not started", "Book-making in progress", "Book files created"]
      : ["Files not started", "Files in progress", "Files created"],
  );

  const publishingChecks = entitlements.includes("paper-kingdom-launch-growth")
    ? [
        Boolean(
          firstListing?.description.trim() &&
            firstListing.audienceDetails.trim() &&
            (!sellingGuide.titleEditable || firstListing.listingTitle.trim()),
        ),
        Boolean(
          firstListing?.keywords
            .slice(0, sellingGuide.searchCount)
            .filter((item) => item.trim()).length === sellingGuide.searchCount,
        ),
        Boolean(
          firstListing?.shortPitch.trim() &&
            firstListing.socialPosts.filter((item) => item.trim()).length === 3 &&
            firstListing.launchEmailSubject.trim() &&
            firstListing.launchEmailBody.trim(),
        ),
        Boolean(
          firstListing?.visualPlan
            .slice(0, sellingGuide.visualCount)
            .filter((item) => item.trim()).length === sellingGuide.visualCount,
        ),
      ]
    : [];
  const publishing = lane(
    publishingChecks,
    ["Selling kit not started", "Selling kit in progress", "Selling kit ready"],
  );

  const promptsPrepared =
    catalogueComplete &&
    project.promptPlanIds.length > 0 &&
    project.promptPlanIds.every((id) => {
      const work = getPromptWorkForChannel(project, id);
      return Boolean(
        work?.status === "skipped" ||
          work?.copiedAt ||
          work?.response.trim(),
      );
    });
  const listingComplete = publishingChecks.slice(0, 2).every(Boolean);

  return {
    checks: [
      ideaComplete,
      catalogueComplete,
      promptsPrepared,
      productionChecks.length > 0 && productionChecks.every(Boolean),
      publishingChecks.length > 0 && listingComplete,
      Boolean(project.lastCompleteHandoffAt),
    ],
    percentage: planning.percentage,
    planning,
    production,
    publishing,
  };
}

export function getProjectReadinessSummary(
  project: PaperKingdomProject,
  entitlements: OfferSku[],
): ReadinessSection[] {
  const firstBook = project.catalogue[0];
  const cover = firstBook ? project.production.coverPlans[firstBook.id] : undefined;
  const interior = firstBook ? project.production.interiorPlans[firstBook.id] : undefined;
  const listing = firstBook ? getChannelListing(project, firstBook.id) : undefined;
  const isBook = getMarketplaceGuide(project.marketplace).usesKdpPrintSetup;
  const sellingGuide = getSellingKitGuide(project.marketplace);
  const itemNoun = isBook ? "book" : "product";
  const primaryFileCurrent = Boolean(
    firstBook &&
      project.production.primaryFileCreated &&
      project.production.primaryFilePromptSignature ===
        getPrimaryFileBlueprintSignature(project, firstBook.id),
  );
  const coverFileCurrent = Boolean(
    firstBook &&
      project.production.coverPdfCreated &&
      project.production.coverFilePromptSignature ===
        getCoverFileBlueprintSignature(project, firstBook.id),
  );

  const startMissing = [
    !project.audience.trim() ? `Describe who the ${itemNoun} is for` : "",
    !project.theme.trim() ? `Describe what the ${itemNoun} should help them do` : "",
  ].filter(Boolean);
  const bookMissing = project.catalogue.flatMap((book) => [
    !book.title.trim() ? `Add a working name for ${itemNoun} ${book.sequence}` : "",
    !book.differentiator.trim() ? `Explain the purpose of ${itemNoun} ${book.sequence}` : "",
  ]).filter(Boolean);
  const promptMissing = project.promptPlanIds.flatMap((id, index) => {
    const work = getPromptWorkForChannel(project, id);
    return work?.status === "skipped" || work?.copiedAt || work?.response.trim()
      ? []
      : [`Use or save recommended prompt ${index + 1}`];
  });

  const makingMissing = [
    !project.production.coverSystemId ? `Choose a ${isBook ? "cover" : "preview"} style` : "",
    !project.production.coverPaletteId ? "Choose the product colours" : "",
    !cover?.backCoverCopy.trim() ? `Write the ${isBook ? "back-cover" : "preview"} message` : "",
    !interior?.layoutIds.length ? "Choose the inside-page layouts" : "",
    !interior?.sequenceNotes.trim() ? "Explain how the pages flow" : "",
    isBook && !primaryFileCurrent ? "Create the inside PDF from the current page plan" : "",
    isBook && !coverFileCurrent ? "Create the cover preview from the current template" : "",
    !isBook && !primaryFileCurrent
      ? `Create the ${getPrimaryFileLabel(project).toLowerCase()}`
      : "",
  ].filter(Boolean);

  const sellingKitComplete = Boolean(
    listing?.description.trim() &&
      listing.audienceDetails.trim() &&
      (!sellingGuide.titleEditable || listing.listingTitle.trim()) &&
      listing.keywords
        .slice(0, sellingGuide.searchCount)
        .filter((item) => item.trim()).length === sellingGuide.searchCount &&
      listing.shortPitch.trim() &&
      listing.socialPosts.filter((item) => item.trim()).length === 3 &&
      listing.launchEmailSubject.trim() &&
      listing.launchEmailBody.trim() &&
      listing.visualPlan
        .slice(0, sellingGuide.visualCount)
        .filter((item) => item.trim()).length === sellingGuide.visualCount,
  );
  const sellingMissing = sellingKitComplete ? [] : ["Create the selling kit"];

  const sections = [
    { title: "Start here", missing: startMissing },
    { title: isBook ? "Book ideas" : "Product ideas", missing: bookMissing },
    { title: "Get prompts", missing: promptMissing },
  ];
  if (entitlements.includes("paper-kingdom-production")) {
    sections.push({ title: isBook ? "Make the book" : "Make the files", missing: makingMissing });
  }
  if (entitlements.includes("paper-kingdom-launch-growth")) {
    sections.push({ title: "Prepare to sell", missing: sellingMissing });
  }

  return sections.map((section) => ({
    ...section,
    complete: section.missing.length === 0,
  }));
}
